import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import {
    faArrowCircleRight,
    faChartColumn,
    faHeartRate,
    faQuestion,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGrid2 } from '@fortawesome/sharp-solid-svg-icons';
import { Button } from 'components/button/Button';
import { SearchTargetPath } from 'components/global_search/SearchTargetPath';
import {
    SearchTargetContainer,
    SearchTargetName,
    TargetIconContainer,
} from 'components/global_search/styled';
import { HardwareIcon } from 'components/svg_icon/HardwareIcon';
import { HighlightedText } from 'components/text/HighlightedText';
import { memo, ReactNode } from 'react';
import * as React from 'react';
import { GlobalSearchIconType, GlobalSearchTargetInfo } from 'utils/global_search';
import { Lookup } from 'utils/types';

const NAV_ICON_COLOR = 'var(--background-multicolors-default)';

const icons: Lookup<GlobalSearchIconType, ReactNode> = {
    dashboard: (
        <TargetIconContainer $color={NAV_ICON_COLOR}>
            <FontAwesomeIcon icon={faChartColumn} />
        </TargetIconContainer>
    ),
    overview: (
        <TargetIconContainer $color={NAV_ICON_COLOR}>
            <FontAwesomeIcon icon={faGrid2} />
        </TargetIconContainer>
    ),
    workbench: (
        <TargetIconContainer $color={NAV_ICON_COLOR}>
            <FontAwesomeIcon icon={faHeartRate} />
        </TargetIconContainer>
    ),
    baseStation: (
        <TargetIconContainer $color={NAV_ICON_COLOR}>
            <HardwareIcon type="baseStation" />
        </TargetIconContainer>
    ),
    node: (
        <TargetIconContainer $color={NAV_ICON_COLOR}>
            <HardwareIcon type="sensor" />
        </TargetIconContainer>
    ),
};

const getIcon = (iconType: GlobalSearchIconType) =>
    iconType in icons ? (
        icons[iconType]
    ) : (
        <TargetIconContainer $color="var(--icon-primary)">
            <FontAwesomeIcon icon={faQuestion} />
        </TargetIconContainer>
    );

export type SearchTargetProps = Readonly<{
    target: GlobalSearchTargetInfo;
    isHighlighted: boolean;
    remoteTokens?: string[];
    localTokens?: string[];
    onClick: () => void;
    onRemove?: () => void;
}>;

function SearchTarget({
    target: { targetName, entityName, iconType, entityPath },
    isHighlighted,
    remoteTokens = [],
    localTokens = [],
    onClick,
    onRemove,
}: SearchTargetProps) {
    const onRemoveClick = onRemove
        ? (event: React.MouseEvent) => {
              event.preventDefault();
              event.stopPropagation();
              onRemove();
          }
        : undefined;

    return (
        <SearchTargetContainer
            $isHighlighted={isHighlighted}
            onClick={onClick}
            aria-label={`search target ${entityName} ${targetName}${
                isHighlighted ? ' highlighted' : ''
            }`}
        >
            <div className="ml-15">{getIcon(iconType)}</div>
            <div className="flex-grow-1 mx-15">
                <SearchTargetName className="mb-3">
                    <HighlightedText text={targetName} terms={localTokens} />
                </SearchTargetName>
                <SearchTargetPath
                    entityName={entityName}
                    entityPath={entityPath}
                    remoteTokens={remoteTokens}
                />
            </div>
            <div className="mr-15">
                {onRemoveClick ? (
                    <Button
                        onClick={onRemoveClick}
                        aria-label={`remove ${entityName} ${targetName} from history`}
                        title="Remove from history"
                    >
                        <FontAwesomeIcon color="var(--icon-default)" icon={faTimes} />
                    </Button>
                ) : (
                    <FontAwesomeIcon
                        color="var(--icon-positive)"
                        icon={faArrowCircleRight}
                    />
                )}
            </div>
        </SearchTargetContainer>
    );
}

const memoSearchTarget = memo(SearchTarget);
export { memoSearchTarget as SearchTarget };
